import React, {MouseEventHandler, useContext, useEffect} from "react";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {useRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import SearchFilters from "../../../common/components/SearchFilters";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const ExceptionListFilters = ({
                                  handleClickSearch,
                                  handleClickResetSearch,
                                  commercialCategoryListOriginal,
                                  testFilters,
                                  setTestFilters
                              }: {
    handleClickSearch: MouseEventHandler<HTMLButtonElement>
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>
    commercialCategoryListOriginal: CommercialCategorySelectFE[]
    testFilters: any
    setTestFilters: any
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState<CommercialCategorySelectFE[]>(commercialCategoryListAtom);
    const [commercialSegmentList] = useRecoilState<SelectElementModelFE[]>(commercialSegmentListAtom);

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    // Filtra commercial categories quando cambiano legal entities o commercial segment
    useEffect(() => {
        // Se nessuna legal entity selezionata, svuota la lista
        if (!testFilters.legalEntities || 
            (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0)) {
            setCommercialCategoryList([]);
            return;
        }

        // Ottieni le legal entities selezionate
        const selectedLegalEntities = Array.isArray(testFilters.legalEntities)
            ? testFilters.legalEntities.map((le: SelectElementModelFE) => le.code as string)
            : [testFilters.legalEntities.code as string];

        // Filtra per commercial segment se selezionato
        let filteredBySegment = commercialCategoryListOriginal;
        if (testFilters.commercialSegmentId) {
            filteredBySegment = commercialCategoryListOriginal.filter(
                cc => cc.commercialSegment.id === (testFilters.commercialSegmentId as SelectElementModelFE).code
            );
        }

        // Espandi le categorie in base alle legal entities selezionate e deduplica per label
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        
        filteredBySegment.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (selectedLegalEntities.includes(labelObj.country)) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });

        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [testFilters.legalEntities, testFilters.commercialSegmentId, commercialCategoryListOriginal]);

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "listName",
            value: testFilters.listName,
            label: `${t("pricing:exceptionList.form.labels.name")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                listName: value
            }),
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegmentId",
            value: testFilters.commercialSegmentId,
            label: `${t("pricing:exceptionList.form.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null,
                });
            },
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId,
            label: `${t("pricing:exceptionList.form.labels.commercialCategory")}`,
            handleChange: (value: CommercialCategorySelectFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                });
            },
            options: commercialCategoryList,
            type: "Autocomplete",
            disabled: !testFilters.legalEntities || 
                     (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0),
        }
    ];
    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "from",
            value: testFilters.from as string,
            label: `${t("pricing:exceptionList.form.labels.startDateAssociation")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                from: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "to",
            value: testFilters.to as string,
            label: `${t("pricing:exceptionList.form.labels.endDateAssociation")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                to: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as any, // Cast per supportare array
            label: `${t("pricing:exceptionList.form.labels.legalEntities")}*`,
            handleChange: (value: SelectElementModelFE[]) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                    commercialCategoryId: null,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false,
            error: !testFilters.legalEntities || 
                   (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0),
            helperText: (!testFilters.legalEntities || 
                        (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0))
                ? t("translation:validation.selectAtLeastOne")
                : undefined,
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:exceptionList.form.labels.dealers")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    return (
        // <CardContainer>
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleClickSearch}
            handleClickResetSearch={handleClickResetSearch}
        />
        // </CardContainer>
    );
};

export default ExceptionListFilters;
